import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HubConnection, HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../services/user/user.service';
import { HeaderComponent } from '../../shared/header/header.component';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { SupervisorService } from '../../../services/supervisor/supervisor.service';
import { TimerConfigService } from '../../../services/timer-config/timer-config.service';
import { CashRegisterService } from '../../../services/cash-register/cash-register.service';
import { SupervisorStatusDto, OperatorMetricsDto, PauseHistoryDto, PeakHourDto, QueueGroupSummaryDto, TicketWindowLockDto, SessionEventDto, PasswordTrailSummaryDto, PasswordTrailEventDto } from '../../../interfaces/supervisor-status.dto';
import { AttendanceMetaDto } from '../../../interfaces/timer-config.dto';
import { CashRegisterAnalysis } from '../../../interfaces/cash-register';
import { BrandService } from '../../../services/brand/brand.service';

@Component({
  selector: 'app-supervisor',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, RouterLink],
  templateUrl: './supervisor.component.html',
})
export class SupervisorComponent implements OnInit, OnDestroy {
  clinicId!: number;
  tab: 'live' | 'metrics' | 'peaks' | 'trail' | 'windows' = 'live';
  dateRef = new Date().toISOString().split('T')[0];

  liveStatus: SupervisorStatusDto[] = [];
  metrics: OperatorMetricsDto[] = [];
  peaks: PeakHourDto[] = [];
  queueSummary: QueueGroupSummaryDto[] = [];
  pauseHistory: PauseHistoryDto[] = [];
  pauseFilterUser = '';
  sessionHistory: SessionEventDto[] = [];
  expandedSessionUsers = new Set<string>();
  trail: PasswordTrailSummaryDto[] = [];
  trailFilter = '';
  trailLoaded = false;
  selectedTrail: PasswordTrailSummaryDto | null = null;
  timeline: PasswordTrailEventDto[] = [];
  timelineView: Array<{
    e: PasswordTrailEventDto; icon: string; titulo: string;
    destaqueSala: boolean; extra: string; maquina?: string;
  }> = [];
  timelineLoading = false;
  meta!: AttendanceMetaDto;
  caixaMap = new Map<string, CashRegisterAnalysis>();

  ticketWindows: TicketWindowLockDto[] = [];
  releasingWindow: string | null = null;
  releasedWindow: string | null = null;
  mensagem: string | null = null;
  mensagemTipo: 'ok' | 'err' | 'info' = 'info';

  isLoading = false;
  private pollTimer: any;
  private pollTick = 0;
  private readonly POLL_INTERVAL = 15_000;
  private readonly FEEDBACK_TIMEOUT = 6_000;

  private liveHub: HubConnection | null = null;
  private liveHubConnected = false;
  private liveReloadDebounce: any = null;
  private static readonly LIVE_DEBOUNCE_MS = 2_000;

  private static readonly LIMITE_SANGRIA = 1000;

  constructor(
    private supervisorService: SupervisorService,
    private timerConfigService: TimerConfigService,
    private localStorageService: LocalStorageService,
    private cashRegisterService: CashRegisterService,
    private brandService: BrandService,
    private userService: UserService,
  ) {
    this.clinicId = this.localStorageService.get('currentUnit');
  }

  async ngOnInit(): Promise<void> {
    this.meta = await this.timerConfigService.getMeta(this.clinicId);
    await this.loadAll();
    this.openLiveHub();
    // Com o hub conectado o poll vira rede de seguranca (efetivo 30s, ticks
    // alternados); sem hub, mantem os 15s de sempre.
    this.pollTimer = setInterval(() => {
      if (this.tab !== 'live') return;
      this.pollTick++;
      if (!this.liveHubConnected || this.pollTick % 2 === 0) this.loadLive();
    }, this.POLL_INTERVAL);
  }

  ngOnDestroy(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.liveReloadDebounce) clearTimeout(this.liveReloadDebounce);
    const hub = this.liveHub;
    this.liveHub = null;
    this.liveHubConnected = false;
    hub?.stop().catch(() => {});
  }

  // ── Hub: o painel escuta os eventos que os guiches ja recebem e recarrega
  // o Ao Vivo na hora (freio de 2s), em vez de esperar o proximo poll. ──
  private openLiveHub(): void {
    const conn = new HubConnectionBuilder()
      .withUrl(environment.hubUrl, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => this.userService.getTokenAuth(),
      })
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: () => 5000 })
      .build();
    this.liveHub = conn;

    const onEvento = () => this.scheduleLiveReload();
    conn.on('Password', onEvento);
    conn.on('AttPassword', onEvento);
    conn.on('RemovePassword', onEvento);
    conn.on('CallToken', onEvento);

    conn.onreconnected(() => {
      if (this.liveHub !== conn) return;
      this.liveHubConnected = true;
      conn.invoke('Subscription', String(this.clinicId)).catch(() => {});
      this.scheduleLiveReload();
    });
    conn.onclose(() => {
      if (this.liveHub === conn) this.liveHubConnected = false;
    });

    conn.start()
      .then(() => {
        if (this.liveHub !== conn) { conn.stop().catch(() => {}); return; }
        this.liveHubConnected = true;
        return conn.invoke('Subscription', String(this.clinicId));
      })
      .catch(() => {
        if (this.liveHub === conn) this.liveHubConnected = false;
        // Sem WebSocket o poll de 15s segue cobrindo; tenta de novo em 30s.
        setTimeout(() => { if (this.liveHub === conn) { conn.start().then(() => {
          if (this.liveHub !== conn) return;
          this.liveHubConnected = true;
          conn.invoke('Subscription', String(this.clinicId)).catch(() => {});
        }).catch(() => {}); } }, 30_000);
      });
  }

  private scheduleLiveReload(): void {
    if (this.tab !== 'live') return;
    if (this.liveReloadDebounce) return;
    this.liveReloadDebounce = setTimeout(() => {
      this.liveReloadDebounce = null;
      if (this.tab === 'live') this.loadLive();
    }, SupervisorComponent.LIVE_DEBOUNCE_MS);
  }

  async setTab(tab: typeof this.tab): Promise<void> {
    this.tab = tab;
    await this.loadAll();
  }

  async setDate(event: Event): Promise<void> {
    this.dateRef = (event.target as HTMLInputElement).value;
    await this.loadAll();
  }

  private async loadAll(): Promise<void> {
    this.isLoading = true;
    try {
      await Promise.all([
        this.tab === 'live'    ? this.loadLive()    : Promise.resolve(),
        this.tab === 'metrics' ? this.loadMetrics() : Promise.resolve(),
        this.tab === 'peaks'   ? this.loadPeaks()   : Promise.resolve(),
        this.tab === 'trail' && this.trailLoaded ? this.loadTrail() : Promise.resolve(),
        this.tab === 'windows' ? this.loadWindows() : Promise.resolve(),
      ]);
    } finally { this.isLoading = false; }
  }

  private async loadLive(): Promise<void> {
    const username = this.localStorageService.get('login') ?? '';
    const [liveStatus, queueSummary, caixas, pauseHistory, metrics] = await Promise.all([
      this.supervisorService.getLiveStatus(this.clinicId),
      this.supervisorService.getQueueSummary(this.clinicId),
      this.cashRegisterService.getAnalysisSummary(username, undefined, this.brandService.cdEmpresa),
      this.supervisorService.getPauseHistory(this.clinicId, this.dateRef),
      this.supervisorService.getMetrics(this.clinicId, this.dateRef).catch(() => [] as typeof this.metrics),
    ]);
    this.queueSummary = queueSummary;
    this.pauseHistory = pauseHistory;
    this.metrics = metrics;
    this.caixaMap = new Map(
      caixas.filter(c => c.username).map(c => [c.username!.toUpperCase(), c])
    );
    // Mescla particulares/plano/média das métricas nos dados ao vivo
    const metricsMap = new Map(metrics.map(m => [m.username.toUpperCase(), m]));
    this.liveStatus = liveStatus.map(s => {
      const m = metricsMap.get(s.username.toUpperCase());
      return m
        ? { ...s, attendedPrivate: m.attendedPrivate, attendedPlan: m.attendedPlan, avgAttendanceSeconds: m.avgAttendanceSeconds }
        : s;
    });
  }

  private async loadMetrics(): Promise<void> {
    [this.metrics, this.pauseHistory, this.sessionHistory] = await Promise.all([
      this.supervisorService.getMetrics(this.clinicId, this.dateRef),
      this.supervisorService.getPauseHistory(this.clinicId, this.dateRef),
      this.supervisorService.getSessionHistory(this.clinicId, this.dateRef),
    ]);
  }
  private async loadPeaks():   Promise<void> { this.peaks   = await this.supervisorService.getPeakHours(this.clinicId, this.dateRef); }

  private async loadTrail(): Promise<void> {
    this.trail = await this.supervisorService.getPasswordTrail(this.clinicId, this.dateRef);
    this.selectedTrail = null;
    this.timeline = [];
  }

  async buscarTrail(): Promise<void> {
    this.isLoading = true;
    try {
      this.trailLoaded = true;
      await this.loadTrail();
    } finally { this.isLoading = false; }
  }

  async openTimeline(t: PasswordTrailSummaryDto): Promise<void> {
    this.selectedTrail = t;
    this.timelineLoading = true;
    try {
      this.timeline = await this.supervisorService.getPasswordTimeline(this.clinicId, t.passwordId);
      this.montarTimelineView();
    } finally { this.timelineLoading = false; }
  }

  private extrairDoDetail(detail: string | undefined, chave: string): string {
    if (!detail) return '';
    const m = detail.match(new RegExp(chave + '=([^\\s·]+)'));
    return m ? m[1] : '';
  }

  private montarTimelineView(): void {
    let numChamada = 0;
    const gerada = this.timeline.find(x => x.eventType === 'SENHA_GERADA')?.occurredAt
      ?? this.selectedTrail?.generatedAt;
    const primeiraChamada = this.timeline
      .find(x => x.eventType === 'CHAMADA' || x.eventType === 'CHAMADA_TASY')?.occurredAt;

    this.timelineView = this.timeline.map(e => {
      switch (e.eventType) {
        case 'SENHA_GERADA': {
          const fila = this.extrairDoDetail(e.detail, 'fila');
          const origem = this.extrairDoDetail(e.detail, 'origem');
          const extra = [fila ? `fila ${fila}` : '', origem === 'guiche' ? 'guichê' : 'totem']
            .filter(Boolean).join(' · ');
          return { e, icon: 'ticket', titulo: 'Gerada', destaqueSala: false, extra };
        }
        case 'CHAMADA': {
          numChamada++;
          const origem = this.timelineOrigem(e);
          const extra = origem === 'manual' && numChamada > 1 ? 'manual (re-chamada)' : origem;
          return { e, icon: 'bell', titulo: `Chamada ${numChamada}`, destaqueSala: false, extra };
        }
        case 'CHAMADA_TASY': {
          numChamada++;
          const partes = (e.detail ?? '').split('·').map(s => s.trim());
          const maquina = partes[1] ?? '';
          const isSala = maquina.toUpperCase().includes('SALA');
          return {
            e, icon: isSala ? 'med' : 'bell', titulo: `Chamada ${numChamada}`,
            destaqueSala: isSala, maquina,
            extra: isSala ? 'chamada de sala — encaminhamento' : 'registro do painel',
          };
        }
        case 'ATENDIMENTO_ABERTO': {
          const atend = e.attendanceId ? ` · atendimento ${e.attendanceId}` : '';
          return { e, icon: 'doc', titulo: 'Atendimento aberto', destaqueSala: false,
                   extra: `via ${this.channelLabel(e.detail)}${atend}` };
        }
        case 'SENHA_FINALIZADA': {
          let extra = this.finalDetail(e);
          if (gerada && primeiraChamada) {
            const seg = Math.round((new Date(primeiraChamada).getTime() - new Date(gerada).getTime()) / 1000);
            if (seg > 0) extra += ` · espera até 1ª chamada: ${this.fmtSec(seg)}`;
          }
          return { e, icon: 'check', titulo: 'Finalizada', destaqueSala: false, extra };
        }
        default:
          return { e, icon: 'dot', titulo: this.timelineEventLabel(e), destaqueSala: false, extra: e.detail ?? '' };
      }
    });
  }

  get trailFiltered(): PasswordTrailSummaryDto[] {
    const f = this.trailFilter.trim().toUpperCase();
    return f ? this.trail.filter(t => t.password.toUpperCase().includes(f)) : this.trail;
  }

  trailStatusLabel(t: PasswordTrailSummaryDto): string {
    const map: Record<string, string> = {
      AGUARDANDO: 'Aguardando', EM_ATENDIMENTO: 'Em atendimento', FINALIZADA: 'Finalizada',
    };
    return map[t.status] ?? t.status;
  }

  trailStatusColor(t: PasswordTrailSummaryDto): string {
    const map: Record<string, string> = {
      AGUARDANDO: 'bg-gray-100 text-gray-600',
      EM_ATENDIMENTO: 'bg-yellow-100 text-yellow-700',
      FINALIZADA: 'bg-green-100 text-green-700',
    };
    return map[t.status] ?? 'bg-gray-100 text-gray-600';
  }

  timelineEventLabel(e: PasswordTrailEventDto): string {
    const map: Record<string, string> = {
      SENHA_GERADA: 'Gerada', CHAMADA: 'Chamada', SENHA_FINALIZADA: 'Finalizada',
      CHAMADA_TASY: 'Chamada (sala/painel)', ATENDIMENTO_ABERTO: 'Atendimento aberto',
    };
    return map[e.eventType] ?? e.eventType;
  }

  channelLabel(canal?: string): string {
    if (!canal) return '—';
    const map: Record<string, string> = {
      'fila-nova': 'Fila', 'fila-antiga': 'Fila antiga', 'pos': 'POS', 'agenda': 'Agenda',
    };
    return map[canal] ?? canal;
  }

  // Quem gerou a senha (PSF.NM_USUARIO). Geradores de sistema recebem rotulo
  // amigavel; qualquer outro valor e o login do operador que gerou.
  geradaPorLabel(gerador?: string): string {
    if (!gerador) return '';
    const map: Record<string, string> = {
      'PAV': 'Autoatendimento', 'TOTEM': 'Check-in totem', 'TELEMED': 'Telemedicina',
    };
    return map[gerador.toUpperCase()] ?? gerador;
  }

  // Geradores automaticos (nao-operador): totem de senha, check-in, telemedicina.
  // Servem de origem no "Aberto via" quando nao houve abertura de atendimento.
  geradorSistema(gerador?: string): boolean {
    if (!gerador) return false;
    return ['PAV', 'TOTEM', 'TELEMED'].includes(gerador.toUpperCase());
  }

  // Eventos antigos gravaram o codigo do motivo; traduz na exibicao
  // (0=atendida ja vem legivel; 8=nao atendida; 9=descartada).
  finalDetail(e: PasswordTrailEventDto): string {
    if (!e.detail) return '';
    return e.detail
      .replace('resultado=inutilizada motivo=8', 'não atendida')
      .replace('resultado=inutilizada motivo=9', 'descartada')
      .replace('resultado=', '');
  }

  timelineOrigem(e: PasswordTrailEventDto): string {
    if (!e.detail) return '';
    if (e.detail.includes('origem=manual')) return 'manual';
    if (e.detail.includes('origem=automatica')) return 'automática';
    return '';
  }

  toggleSessionUser(username: string): void {
    if (this.expandedSessionUsers.has(username)) this.expandedSessionUsers.delete(username);
    else this.expandedSessionUsers.add(username);
  }

  isSessionUserExpanded(username: string): boolean {
    return this.expandedSessionUsers.has(username);
  }

  sessionEventLabel(e: SessionEventDto): string {
    const map: Record<string, string> = {
      LOGIN: 'Login', LOGOFF: 'Logoff', NAVEGACAO: 'Navegação', SESSAO_FIM: 'Saída de sessão',
    };
    return map[e.eventType] ?? e.eventType;
  }

  sessionEventColor(e: SessionEventDto): string {
    const map: Record<string, string> = {
      LOGIN: 'text-green-600', LOGOFF: 'text-orange-500',
      NAVEGACAO: 'text-blue-400', SESSAO_FIM: 'text-orange-500',
    };
    return map[e.eventType] ?? 'text-gray-500';
  }

  sessionsByOperator(): { username: string; events: SessionEventDto[]; logins: number; ends: number; navs: number }[] {
    const filtered = this.pauseFilterUser
      ? this.sessionHistory.filter(e => e.username === this.pauseFilterUser)
      : this.sessionHistory;
    const map = new Map<string, SessionEventDto[]>();
    for (const e of filtered) {
      if (!map.has(e.username)) map.set(e.username, []);
      map.get(e.username)!.push(e);
    }
    return Array.from(map.entries())
      .map(([username, events]) => ({
        username,
        events,
        logins: events.filter(e => e.eventType === 'LOGIN').length,
        ends: events.filter(e => e.eventType === 'SESSAO_FIM' || e.eventType === 'LOGOFF').length,
        navs: events.filter(e => e.eventType === 'NAVEGACAO').length,
      }))
      .sort((a, b) => a.username.localeCompare(b.username));
  }

  fmtHourSec(iso: string): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  private async loadWindows(): Promise<void> { this.ticketWindows = await this.supervisorService.getTicketWindows(this.clinicId); }

  // ── Liberar guichê ────────────────────────────────────────────────────────

  async releaseWindow(w: TicketWindowLockDto): Promise<void> {
    this.releasingWindow = w.name;
    this.releasedWindow = null;
    try {
      const feedback = await this.supervisorService.releaseTicketWindow(this.clinicId, w.name);
      await this.loadWindows();
      this.toast(feedback, 'ok');
      // Destaca a linha liberada para confirmar visualmente qual guichê foi afetado
      this.releasedWindow = w.name;
      setTimeout(() => { if (this.releasedWindow === w.name) this.releasedWindow = null; }, this.FEEDBACK_TIMEOUT);
    } catch (e: any) {
      this.toast(e?.error?.feedback || 'Erro ao liberar o guichê. Tente novamente.', 'err');
    } finally {
      this.releasingWindow = null;
    }
  }

  private toast(msg: string, tipo: 'ok' | 'err' | 'info' = 'info'): void {
    this.mensagem = msg;
    this.mensagemTipo = tipo;
    setTimeout(() => { if (this.mensagem === msg) this.mensagem = null; }, this.FEEDBACK_TIMEOUT);
  }

  get lockedWindows(): number { return this.ticketWindows.filter(w => w.isLocked).length; }
  get onlineWindows(): number { return this.ticketWindows.filter(w => w.isOnline).length; }

  // ── Helpers ───────────────────────────────────────────────────────────────

  get totalWaiting(): number { return this.queueSummary.reduce((s, g) => s + g.waitingCount, 0); }
  get activeOperators(): number { return this.liveStatus.filter(s => s.isOnline).length; }
  get pausedOperators(): number { return this.liveStatus.filter(s => s.isOnline && s.cycleStatus === 'PAUSADO').length; }
  get availableOperators(): number { return this.activeOperators - this.pausedOperators; }
  get todayTotal(): number { return this.liveStatus.length; }
  get loadPerOperator(): number {
    return this.availableOperators > 0 ? Math.round(this.totalWaiting / this.availableOperators * 10) / 10 : 0;
  }

  groupColor(letter: string): string {
    const map: Record<string, string> = {
      PR: '#ef4444', P: '#f97316', L: '#3b82f6', EX: '#8b5cf6', R: '#6b7280',
    };
    return map[letter.toUpperCase()] ?? '#7c3aed';
  }

  statusLabel(s: SupervisorStatusDto): string {
    if (!s.isOnline) return 'Offline';
    const map: Record<string, string> = {
      CONTAGEM: 'Aguardando senha', CHAMADO: 'Em atendimento',
      PAUSADO: 'Pausado', FINALIZADO: 'Finalizando', OCIOSO: 'Ocioso',
    };
    return map[s.cycleStatus] ?? s.cycleStatus;
  }

  statusColor(s: SupervisorStatusDto): string {
    if (!s.isOnline) return 'bg-gray-100 text-gray-400';
    const map: Record<string, string> = {
      CONTAGEM: 'bg-blue-100 text-blue-700',
      CHAMADO:  'bg-green-100 text-green-700',
      PAUSADO:  'bg-yellow-100 text-yellow-700',
      OCIOSO:   'bg-red-100 text-red-600',
      FINALIZADO: 'bg-gray-100 text-gray-600',
    };
    return map[s.cycleStatus] ?? 'bg-gray-100 text-gray-600';
  }

  fmtSec(sec: number): string {
    const total = Math.round(sec ?? 0);
    if (total < 60) return `${total}s`;
    const m = Math.floor(total / 60), s = total % 60;
    return `${m}m ${s.toString().padStart(2,'0')}s`;
  }

  /** Guichês consolidados vêm do back como "A / B / C" — vira um chip por guichê. */
  splitMachines(machine: string | null | undefined): string[] {
    if (!machine) return ['—'];
    return machine.split(' / ').map(m => m.trim()).filter(m => m.length > 0);
  }

  fmtSecLong(sec: number): string {
    const total = Math.round(sec ?? 0);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h) return `${h}h ${m}m`;
    return `${m}m`;
  }

  targetPct(m: OperatorMetricsDto): number {
    if (!this.meta?.dailyTarget) return 0;
    return Math.min(100, Math.round((m.attendedCount / this.meta.dailyTarget) * 100));
  }

  peakBar(p: PeakHourDto): number {
    const max = Math.max(...this.peaks.map(x => x.passwordCount), 1);
    return Math.round((p.passwordCount / max) * 100);
  }

  isIdle(s: SupervisorStatusDto): boolean {
    return s.isOnline && s.idleSeconds > 600;
  }

  caixaOf(username: string): CashRegisterAnalysis | undefined {
    return this.caixaMap.get(username.toUpperCase());
  }

  caixaPrecisaSangrar(c: CashRegisterAnalysis): boolean {
    return c.isOpen && c.currentBalance > SupervisorComponent.LIMITE_SANGRIA;
  }

  formatCurrency(v: number): string {
    return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  get pauseOperators(): string[] {
    return [...new Set(this.pauseHistory.map(p => p.username))].sort();
  }

  pausesByOperator(): { username: string; pauses: PauseHistoryDto[] }[] {
    const filtered = this.pauseFilterUser
      ? this.pauseHistory.filter(p => p.username === this.pauseFilterUser)
      : this.pauseHistory;
    const map = new Map<string, PauseHistoryDto[]>();
    for (const p of filtered) {
      if (!map.has(p.username)) map.set(p.username, []);
      map.get(p.username)!.push(p);
    }
    return Array.from(map.entries())
      .map(([username, pauses]) => ({ username, pauses }))
      .sort((a, b) => a.username.localeCompare(b.username));
  }

  totalPausedByOperator(username: string): number {
    return this.pauseHistory
      .filter(p => p.username === username && p.durationSeconds != null)
      .reduce((s, p) => s + (p.durationSeconds ?? 0), 0);
  }

  fmtHour(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  pauseSummaryForUser(username: string): { reason: string; totalSec: number }[] {
    const map = new Map<string, number>();
    for (const p of this.pauseHistory.filter(p => p.username === username && p.durationSeconds)) {
      map.set(p.pauseReason, (map.get(p.pauseReason) ?? 0) + (p.durationSeconds ?? 0));
    }
    return Array.from(map.entries())
      .map(([reason, totalSec]) => ({ reason, totalSec }))
      .sort((a, b) => b.totalSec - a.totalSec);
  }

  fmtMin(sec: number): string {
    const m = Math.round(sec / 60);
    return m < 1 ? '<1min' : `${m}min`;
  }
}
